<template>
  <div class="self-stretch flex flex-col gap-1 px-3 py-2 mb-2 rounded bg-transparent outline outline-1 outline-border-separator"
       data-test="scheduled-fork-banner">
    <div class="self-stretch inline-flex items-center gap-2">
      <div class="flex-1 font-sans text-xs font-bold uppercase tracking-[0.06em] text-text-subtle" data-test="origin">
        {{ $t('messaging.scheduledFork.origin') }}
      </div>
    </div>
    <div class="self-stretch font-sans text-xs text-text-subtle" data-test="ran-at">
      <span v-if="run_at_label">{{ run_at_label }}</span>
      <span v-else>{{ $t('messaging.scheduledFork.originNoTime') }}</span>
    </div>
    <button v-if="fromThreadId" type="button" data-test="back-link"
            class="self-start font-sans text-xs text-text-link cursor-pointer bg-transparent"
            @click="$emit('openOrigin', fromThreadId)">
      {{ $t('messaging.scheduledFork.backToScheduled') }}
    </button>
  </div>
</template>

<script>
/**
 * The origin banner at the top of a forked thread: where this conversation came from, when the
 * finding it holds was true, and a way back to the Scheduled tasks thread.
 *
 * Rendered from metadata on the thread's FIRST message — the seeded copy of the result — rather than
 * from a field on the thread. The banner is specified as sitting at the top of the thread, and in a
 * fork the seed is what sits there, so no thread-level field was needed to put it in the right place.
 *
 * Deliberately not a result card. The seed is a COPY of a result, in a thread that is no longer the
 * record of that task: later runs keep posting to the Scheduled tasks thread. Giving the copy an
 * outcome pill and a freshness stamp would present a snapshot as though it were still live.
 *
 * The back link is emitted, not navigated: only MessengerWindow knows how to open a Rey conversation
 * (SPARK-OPEN-CONVERSATION, and its dual-mount guard), and a component this small should not.
 */
export default {
  name: 'ScheduledForkBanner',
  emits: ['openOrigin'],
  props: {
    /** The Scheduled tasks thread this was forked out of. Empty hides the back link. */
    fromThreadId: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * ISO-8601 UTC of when the originating RUN produced the result — not when the fork was made.
     * Absent on a result delivered before that key existed.
     */
    runAt: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    /**
     * When the finding was true, in the VIEWER's zone via the browser's own formatter.
     *
     * An unparseable or absent value falls back to a sentence with no time in it rather than to a
     * guess — a fork of an older result is still worth labelling as a fork.
     */
    run_at_label() {
      if (!this.runAt) return '';
      const when = new Date(this.runAt);
      if (Number.isNaN(when.getTime())) return '';

      try {
        return this.$t('messaging.scheduledFork.ranAt', {
          time: when.toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          }),
        });
      } catch (e) {
        return '';
      }
    },
  },
};
</script>
